<template>
	<div class="overflow-y-auto contents">
		<!-- mobile tabs -->
		<SessionContentMobileTabs v-if="isMobile" class="mx-16 mt-16 sm:hidden" />

		<SplitPanels
			v-if="session"
			:defaultColumns="$sessionLayout.columns"
			:localStorageKey="$sessionLayout.localStorageKey"
			:options="{minSize: $sessionLayout.minSize}"
			class="flex-grow min-h-0"
		>
			<!-- LEFT PANEL -->
			<SessionContentPanel v-if="!(isMobile && $sessionLayout.mobileTab !== 'media')" data-test="media-panel">
				<div class="w-full h-full overflow-auto p-16 sm:p-24 visible-scrollbars">
					<div class="mx-auto lg:max-w-70vw">
						<SessionMedia
							class="pb-16"
							@expand="$sessionLayout.expandMediaPanel"
							@minimize="$sessionLayout.minimizeMediaPanel"
						/>
						<SessionTitleDescription ref="sessionTitleDescription" class="mb-24" />
						<SessionComments v-if="!$store.isExtension" data-test="comments" />
					</div>
				</div>
			</SessionContentPanel>
			<!-- CENTER PANEL  -->
			<SessionContentPanel
				v-if="isMobile ? $sessionLayout.mobileTab === 'devTools' : !$sessionLayout.devToolsHidden"
				data-test="dev-tools-panel"
				@close="$sessionLayout.toggleDevToolsPanel({from: 'close button'})"
			>
				<template #title> Developer Tools </template>
				<template v-if="!session.usageBasedSubscriptionPlan || $store.isUserWorkspaceMember" #title-actions>
					<SessionDevToolSettingsPopover
						v-if="['console', 'network'].includes($sessionLayout.devToolsPane) && !isMobile"
						class="ml-auto mr-4"
					/>
				</template>
				<!-- Users need to be signed in to view the dev tools for sessions from workspaces on usage-based plans -->
				<div
					v-if="session.usageBasedSubscriptionPlan === true && !$auth.isAuthenticated.value"
					class="p-16 sm:p-24 text-gray-400"
				>
					Please
					<button v-if="!$auth.isAuthenticated.value" class="link-blue" @click="login">log in</button>
					to view console logs, network requests, system information, and E2E helpers.
				</div>
				<CanUserJoinWorkspace v-else-if="showCanUserJoinWorkspace" #default="{workspaceUserCanJoin}">
					<!-- Furthermore, if users can join the workspace, they need to do so to view the dev tools - on all plans -->
					<SessionJoinWorkspaceToViewDevTools
						v-if="workspaceUserCanJoin && workspaceUserCanJoin.id === session.workspaceId"
						:workspaceId="session.workspaceId"
					/>
					<!-- Users who can't join the workspace see the dev tools -->
					<SessionDevTools v-else />
				</CanUserJoinWorkspace>
				<!-- Workspace members see the dev tools -->
				<SessionDevTools v-else />
			</SessionContentPanel>
			<!-- RIGHT PANEL  -->
			<template v-if="isMobile ? $sessionLayout.mobileTab === 'details' : !$sessionLayout.detailsHidden">
				<!-- integrations -->
				<SessionIntegrationFormJira
					v-if="jiraPreconditionsFulfilled"
					:session="session"
					@close="closeIntegrationForm('jira')"
					@success="closeIntegrationForm('jira', {replace: true})"
				/>
				<SessionIntegrationFormGithub
					v-else-if="githubPreconditionsFulfilled"
					:session="session"
					@close="closeIntegrationForm('github')"
					@success="closeIntegrationForm('github', {replace: true, reload: true})"
				/>
				<SessionIntegrationFormAzure
					v-else-if="azurePreconditionsFulfilled"
					:session="session"
					@close="closeIntegrationForm('azure')"
					@success="closeIntegrationForm('azure', {replace: true, reload: true})"
				/>
				<SessionIntegrationFormLinear
					v-else-if="linearPreconditionsFulfilled"
					:session="session"
					@close="closeIntegrationForm('linear')"
					@success="closeIntegrationForm('linear', {replace: true, reload: true})"
				/>
				<!-- session details panel -->
				<SessionContentPanel
					v-else
					data-test="details-panel"
					@close="$sessionLayout.toggleDetailsPanel({from: 'close button'})"
				>
					<template #title> Details </template>
					<SessionDetails class="px-16 sm:px-24" />
				</SessionContentPanel>
			</template>
		</SplitPanels>
	</div>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import {storeToRefs} from 'pinia'
import SessionMedia from 'shared/components/SessionMedia.vue'
import SessionTitleDescription from 'shared/components/SessionTitleDescription.vue'
import SessionComments from 'shared/components/SessionComments.vue'
import SessionContentPanel from 'shared/components/SessionContentPanel.vue'
import SessionDetails from 'shared/components/SessionDetails.vue'
import SessionDevTools from 'shared/components/SessionDevTools.vue'
import SessionDevToolSettingsPopover from 'shared/components/SessionDevToolSettingsPopover.vue'
import SplitPanels from 'shared/components/SplitPanels.vue'

import analytics from 'shared/utils/analytics'
import {useWindowSize} from 'shared/composables/useWindowSize'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'SessionContent',
	components: {
		SessionMedia,
		SessionComments,
		SessionTitleDescription,
		SessionContentPanel,
		SessionDetails,
		SessionDevTools, // always bundled, because it accounts for the majorty of session views, and its large children are loaded async
		SessionDevToolSettingsPopover,
		SplitPanels,
		SessionContentMobileTabs: defineAsyncComponent(() => import('shared/components/SessionContentMobileTabs.vue')),
		CanUserJoinWorkspace: defineAsyncComponent(() => import('shared/components/CanUserJoinWorkspace.vue')),
		SessionJoinWorkspaceToViewDevTools: defineAsyncComponent(() =>
			import('shared/components/SessionJoinWorkspaceToViewDevTools.vue')
		),
		SessionIntegrationFormJira: defineAsyncComponent(() =>
			import('shared/components/SessionIntegrationFormJira.vue')
		),
		SessionIntegrationFormGithub: defineAsyncComponent(() =>
			import('shared/components/SessionIntegrationFormGithub.vue')
		),
		SessionIntegrationFormAzure: defineAsyncComponent(() =>
			import('shared/components/SessionIntegrationFormAzure.vue')
		),
		SessionIntegrationFormLinear: defineAsyncComponent(() =>
			import('shared/components/SessionIntegrationFormLinear.vue')
		),
	},
	beforeRouteLeave() {
		// prevent dev tools being hidden from affecting other sessions
		this.$routerHelpers.replaceQueryParams({devToolsHidden: null, detailsHidden: null})
	},
	emits: ['refetchSession'],
	setup() {
		const sessionStore = useSessionStore()
		const {session, canSeek, domTrimming} = storeToRefs(sessionStore)
		const {isMobile} = useWindowSize()
		return {
			session,
			canSeek,
			seek: sessionStore.seek,
			domTrimming,
			isMobile,
		}
	},
	computed: {
		jiraPreconditionsFulfilled() {
			return this.$store.isJiraIntegrationEnabled && this.$route.query.jira === 'create-issue'
		},
		githubPreconditionsFulfilled() {
			return this.$store.isGithubIntegrationEnabled && this.$route.query.github === 'create-issue'
		},
		azurePreconditionsFulfilled() {
			return this.$store.isAzureIntegrationEnabled && this.$route.query.azure === 'create-issue'
		},
		linearPreconditionsFulfilled() {
			return this.$store.isLinearIntegrationEnabled && this.$route.query.linear === 'create-issue'
		},
		showCanUserJoinWorkspace() {
			return (
				this.$auth.isAuthenticated.value &&
				!this.$store.loadingUserWorkspaces &&
				!this.$store.isUserWorkspaceMember && // compare to `false`, because `undefined` while user workspaces are loaded
				!this.$store.isExtension
			)
		},
	},
	watch: {
		'$route.query.seekSeconds': {
			async handler(seconds) {
				this.onSeek(seconds)
			},
		},
		'domTrimming.trimmedStartTimeInMs': {
			immediate: true,
			handler(timeInMs) {
				if (!timeInMs) return
				this.onSeek(timeInMs / 1000)
			},
		},
	},
	mounted() {
		window.addEventListener('playerInitialized', this.onPlayerInitialized)
		window.addEventListener('updateSeekSeconds', this.onSeek)
		window.addEventListener('seek', this.onSeek)
	},
	unmounted() {
		window.removeEventListener('playerInitialized', this.onPlayerInitialized)
		window.removeEventListener('updateSeekSeconds', this.onSeek)
		window.removeEventListener('seek', this.onSeek)
	},
	methods: {
		onPlayerInitialized() {
			if (!this.$route.query.seekSeconds) return
			window.dispatchEvent(new CustomEvent('seek', {detail: this.$route.query.seekSeconds}))
		},
		onSeek(eventOrNumber) {
			let seekSeconds = eventOrNumber?.detail || eventOrNumber
			if (!this.canSeek) return
			if (seekSeconds === null || seekSeconds === undefined) return
			seekSeconds = Number(seekSeconds)
			if (isNaN(seekSeconds)) return
			this.seek(seekSeconds)
		},
		closeIntegrationForm(integration, {replace = false, reload = false} = {}) {
			if (!integration) return
			replace
				? this.$routerHelpers.replaceQueryParams({[integration]: null})
				: this.$routerHelpers.pushQueryParams({[integration]: null})
			if (reload) this.$emit('refetchSession')
		},
		login() {
			analytics.track('Sign In Button Clicked To View Session Dev Tools')
			this.$auth.login({
				redirectAfterLogin: this.$route.fullPath,
			})
		},
	},
}
</script>

<style>
@screen lg {
	.lg\:max-w-70vw {
		max-width: 70vw;
	}
}
</style>
